import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Period, TimePeriod } from '@/types/date';
import { isPeriod } from '@/utils/date/isPeriod';
import cx from 'classnames';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import DateRangePickerPortal, { DateSelection } from './DateRangePickerPortal';
import DateRangeTextInput from './DateRangeTextInput';
import styles from './DateRangeInput.module.css';
import {
  autoFormatDateRangeInput,
  formatDateRangeString,
  formatPartialDateRange,
  formatPeriodValue,
  isCompleteDateRange,
  parseDateRange,
  validateDateRange,
  validateInputInProgress,
  ValidationError,
} from './dateRangeUtils';
import { useDateRangeValidation } from './useDateRangeValidation';

dayjs.extend(customParseFormat);

interface DateRangeInputProps {
  name: string;
  label: string;
  className?: string;
}

interface PickerPosition {
  top: number;
  left: number;
}

const VERTICAL_SPACING = 150;
const VERTICAL_SPACING_BIG = 350;
const WIDGET_WIDTH = 400;

const DateRangeInput: FC<DateRangeInputProps> = ({ name, label, className }) => {
  const { watch, setValue, trigger, clearErrors } = useFormContext<Record<string, unknown>>();
  const watchedValue = watch(name);
  const currentValue = isPeriod(watchedValue) ? watchedValue : undefined;

  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<ValidationError>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState<PickerPosition>({ top: 0, left: 0 });

  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const originalValueRef = useRef<Period | undefined>(undefined);

  const { applyValidation, translateError } = useDateRangeValidation({ name, setValidationError });

  const handleCloseDatePicker = useCallback(() => {
    if (originalValueRef.current === undefined && currentValue) {
      setValue(name, undefined, { shouldDirty: false });
    }
    setShowDatePicker(false);
  }, [currentValue, name, setValue]);

  const pickerRef = useOnClickOutside(() => {
    if (showDatePicker) handleCloseDatePicker();
  }, showDatePicker);

  useEffect(() => {
    if (currentValue?.beginn && currentValue?.end) {
      setInputValue(formatPeriodValue(currentValue));
      setValidationError({});
      clearErrors(name);
    } else if (!currentValue || (!currentValue.beginn && !currentValue.end)) {
      setInputValue('');
      setValidationError({});
      clearErrors(name);
    }
  }, [currentValue, clearErrors, name]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(prevInputValue => {
        const formattedValue = autoFormatDateRangeInput(e.target.value, prevInputValue);

        const inProgressError = validateInputInProgress(formattedValue);
        if (inProgressError.message) {
          applyValidation(inProgressError);
          return formattedValue;
        }

        if (isCompleteDateRange(formattedValue)) {
          const { start, end } = parseDateRange(formattedValue);
          const error = validateDateRange(start, end);
          applyValidation(error);
          if (!error.message && start && end) {
            setValue(name, { beginn: start, end, period: TimePeriod.CUSTOM }, { shouldDirty: true });
            trigger(name);
            return formatDateRangeString(start, end);
          }
        } else {
          applyValidation({});
        }

        return formattedValue;
      });
    },
    [applyValidation, name, setValue, trigger],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      setInputValue(prevInputValue => {
        const formattedValue = autoFormatDateRangeInput(e.clipboardData.getData('text'), prevInputValue);

        const inProgressError = validateInputInProgress(formattedValue);
        if (inProgressError.message) {
          applyValidation(inProgressError);
          return formattedValue;
        }

        if (isCompleteDateRange(formattedValue)) {
          const { start, end } = parseDateRange(formattedValue);
          const error = validateDateRange(start, end);
          applyValidation(error);
          if (!error.message && start && end) {
            setValue(name, { beginn: start, end, period: TimePeriod.CUSTOM }, { shouldDirty: true });
            trigger(name);
            return formatDateRangeString(start, end);
          }
        } else {
          applyValidation({});
        }

        return formattedValue;
      });
    },
    [applyValidation, name, setValue, trigger],
  );

  const handleInputBlur = useCallback(() => {
    const { start, end } = parseDateRange(inputValue);
    const error = validateDateRange(start, end);
    applyValidation(error);
    if (start && end) setInputValue(formatDateRangeString(start, end));
    if (!error.message && start && end) {
      setValue(name, { beginn: start, end, period: TimePeriod.CUSTOM }, { shouldDirty: true });
      trigger(name);
    }
  }, [inputValue, name, setValue, trigger, applyValidation]);

  const handleCalendarClick = useCallback(() => {
    originalValueRef.current = currentValue;

    if (calendarButtonRef.current && !showDatePicker) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      const spacing = window.innerHeight < 900 ? VERTICAL_SPACING_BIG : VERTICAL_SPACING;
      setPickerPosition({
        top: rect.bottom + window.scrollY - spacing,
        left: rect.right + window.scrollX - WIDGET_WIDTH,
      });
    }

    setShowDatePicker(prev => !prev);
  }, [currentValue, showDatePicker]);

  const handleAcceptDateRange = useCallback(
    (period: Period) => {
      setInputValue(formatDateRangeString(period.beginn, period.end));
      setValidationError({});
      clearErrors(name);
      setValue(name, period, { shouldDirty: true });
      trigger(name);
      originalValueRef.current = period;
      setShowDatePicker(false);
    },
    [name, setValue, trigger, clearErrors],
  );

  const handleDateSelectionChange = useCallback((selection: DateSelection) => {
    setInputValue(formatPartialDateRange(selection.startDate, selection.endDate));
  }, []);

  const handleClearSelection = useCallback(() => {
    setInputValue('');
    setValidationError({});
    clearErrors(name);
    setValue(name, undefined, { shouldDirty: true });
  }, [name, setValue, clearErrors]);

  return (
    <div className={cx(styles.container, className)}>
      <DateRangeTextInput
        name={name}
        label={label}
        inputValue={inputValue}
        errorMessageText={translateError(validationError)}
        calendarButtonRef={calendarButtonRef}
        onChange={handleInputChange}
        onPaste={handlePaste}
        onBlur={handleInputBlur}
        onCalendarClick={handleCalendarClick}
      />
      <DateRangePickerPortal
        showDatePicker={showDatePicker}
        currentValue={currentValue}
        pickerRef={pickerRef}
        pickerPosition={pickerPosition}
        handleCloseDatePicker={handleCloseDatePicker}
        handleAcceptDateRange={handleAcceptDateRange}
        onDateSelectionChange={handleDateSelectionChange}
        onClear={handleClearSelection}
      />
    </div>
  );
};

export { DateRangeInput };
